import type { ICollectionDetails } from "~/hooks/useCollections"
import { isAfter, isBefore, startOfTodayUTC } from "~/utils/datetime"

export type CollectionStatus = "Upcoming" | "Active" | "Expired"

export const calculateCollectionStatus = (
  startDate: Date | null,
  endDate: Date | null,
): CollectionStatus => {
  const now = startOfTodayUTC()
  if (startDate && isBefore(now, startDate)) {
    return "Upcoming"
  } else if (endDate && isAfter(now, endDate)) {
    return "Expired"
  } else {
    return "Active"
  }
}

type CollectionStatusInput = Pick<ICollectionDetails, "startDate" | "endDate">

export const isCollectionEnded = (c: CollectionStatusInput) =>
  calculateCollectionStatus(c.startDate, c.endDate) === "Expired"

const fullyOutKey = (variantId: string, collectionId: number) => `${variantId}::${collectionId}`

export const findFullyOutCollections = (
  matchVariantId: string,
  collectionIds: number[] | null | undefined,
  fullyOutSet: Set<string> | undefined,
  collections: Pick<ICollectionDetails, "id" | "name">[] | undefined,
): { id: number; name: string }[] => {
  if (!collectionIds?.length || !fullyOutSet?.size || !collections?.length) return []
  const out: { id: number; name: string }[] = []
  for (const id of collectionIds) {
    if (!fullyOutSet.has(fullyOutKey(matchVariantId, id))) continue
    const c = collections.find((col) => col.id === id)
    if (c) out.push({ id: c.id, name: c.name })
  }
  return out
}

/**
 * Narrows a baseline collection scope to the collections that contain the variant. Falls back to
 * the full scope when none do, so an unfinished baseline collection is still flagged.
 */
export const narrowScopeToVariantCollections = (
  variantId: string,
  scope: number[] | null | undefined,
  collections: Pick<ICollectionDetails, "id" | "variantIds">[] | undefined,
): number[] => {
  if (!scope?.length) return []
  const narrowed = scope.filter((id) =>
    collections?.find((c) => c.id === id)?.variantIds.includes(variantId),
  )
  return narrowed.length > 0 ? narrowed : scope
}

export const findUnfinishedCollections = (
  collectionIds: number[] | null | undefined,
  collections: Pick<ICollectionDetails, "id" | "name" | "startDate" | "endDate">[] | undefined,
): { id: number; name: string; status: CollectionStatus }[] => {
  if (!collectionIds?.length || !collections?.length) return []
  const out: { id: number; name: string; status: CollectionStatus }[] = []
  for (const id of collectionIds) {
    const c = collections.find((col) => col.id === id)
    if (!c) continue
    const status = calculateCollectionStatus(c.startDate, c.endDate)
    if (status !== "Expired") out.push({ id: c.id, name: c.name, status })
  }
  return out
}
